import React, { useState, useCallback } from 'react';
import {
    Box, Typography, Button, Card, CardContent, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Alert, Chip, TextField, Tabs, Tab,
    InputAdornment, Collapse, IconButton,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import PlanningVsOhw from './PlanningVsOhw';
import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL } from '../../utils/apiConfig';
import PageHeader from '../../ui-components/PageHeader';

const formatEuro = (v) => {
    if (!v && v !== 0) return '—';
    return `\u20AC${Number(v).toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

function SummaryCard({ label, value, color = '#333', sub }) {
    return (
        <Card sx={{ flex: '1 1 160px', minWidth: 140 }}>
            <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
                <Typography variant="caption" sx={{ color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {label}
                </Typography>
                {sub && <Typography variant="caption" display="block" sx={{ color, mt: 0.5 }}>{sub}</Typography>}
            </CardContent>
        </Card>
    );
}

function ProjectTable({ projects, columns, search, emptyText = 'Geen resultaten' }) {
    const filtered = search
        ? projects.filter(p => {
            const text = columns.map(c => String(c.value(p) || '')).join(' ').toLowerCase();
            return text.includes(search.toLowerCase());
        })
        : projects;

    if (filtered.length === 0) {
        return <Typography sx={{ p: 2, color: '#999' }}>{emptyText}</Typography>;
    }

    return (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {columns.map((c, i) => (
                            <TableCell key={i} sx={{ fontWeight: 600, ...(c.align ? { textAlign: c.align } : {}) }}>
                                {c.header}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filtered.map((p, idx) => (
                        <TableRow key={idx} hover>
                            {columns.map((c, i) => (
                                <TableCell key={i} sx={c.align ? { textAlign: c.align } : {}}>
                                    {c.render ? c.render(p) : c.value(p)}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function CollapsibleSection({ title, count, color, defaultOpen = true, totalValue, children }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Paper sx={{ mb: 3, overflow: 'hidden' }}>
            <Box
                sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 2, py: 1.5, cursor: 'pointer', borderBottom: open ? '2px solid' : 'none',
                    borderColor: color, bgcolor: '#fafafa',
                }}
                onClick={() => setOpen(!open)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
                    <Chip label={count} size="small" sx={{ bgcolor: color, color: '#fff', fontWeight: 600 }} />
                    {totalValue != null && (
                        <Typography variant="body2" sx={{ color: '#666' }}>{formatEuro(totalValue)}</Typography>
                    )}
                </Box>
                <IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
            </Box>
            <Collapse in={open}>
                <Box sx={{ p: 2 }}>{children}</Box>
            </Collapse>
        </Paper>
    );
}

export default function OHWCheck() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [tab, setTab] = useState(0);
    const [dragOver, setDragOver] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [downloading, setDownloading] = useState(false);

    const handleUpload = useCallback(async (file) => {
        if (!file) return;
        setLoading(true);
        setError(null);
        setData(null);
        setUploadedFile(file);

        try {
            const formData = new FormData();
            formData.append('excel', file);

            const res = await authFetch(`${BACKEND_URL}/Api/ohw/check`, {
                method: 'POST',
                body: formData,
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Upload mislukt (${res.status})`);
            }

            const result = await res.json();
            setData(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleDownloadAnnotated = useCallback(async () => {
        if (!uploadedFile) return;
        setDownloading(true);
        setError(null);
        try {
            const formData = new FormData();
            formData.append('excel', uploadedFile);

            const res = await authFetch(`${BACKEND_URL}/Api/ohw/annotate`, {
                method: 'POST',
                body: formData,
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Download mislukt (${res.status})`);
            }

            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'OnderhandenWerk_FF_metDeltas.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message);
        } finally {
            setDownloading(false);
        }
    }, [uploadedFile]);

    const handleFileInput = (e) => {
        const file = e.target.files?.[0];
        if (file) handleUpload(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleUpload(file);
    };

    // Column definitions
    const missingCols = [
        { header: 'Nr', value: p => p.projectnummer, render: p => <strong>{p.projectnummer}</strong> },
        { header: 'Klant', value: p => Array.isArray(p.klant) ? p.klant[0] : p.klant },
        { header: 'Project', value: p => p.projectnaam },
        { header: 'Status', value: p => p.status, render: p => (
            <Chip label={p.status} size="small" sx={{
                bgcolor: p.status === 'In progress' ? '#dc354520' : p.status === 'On Hold' ? '#fd7e1420' : '#6c757d20',
                color: p.status === 'In progress' ? '#dc3545' : p.status === 'On Hold' ? '#fd7e14' : '#6c757d',
                fontWeight: 500, fontSize: 12,
            }} />
        )},
        { header: 'PL', value: p => p.projectleider || '—' },
        { header: 'Waarde', value: p => p.opdrachtwaarde, render: p => formatEuro(p.opdrachtwaarde), align: 'right' },
    ];

    const excelNotAirtableCols = [
        { header: 'Nr', value: p => p.projectnummer, render: p => <strong>{p.projectnummer}</strong> },
        { header: 'Omschrijving', value: p => p.omschrijving },
        { header: 'PL', value: p => p.pl || '—' },
        { header: 'Waarde', value: p => p.opdrachtwaarde, render: p => formatEuro(p.opdrachtwaarde), align: 'right' },
        { header: 'Excel status', value: p => p.status || '—' },
    ];

    const forecastCols = [
        { header: 'Nr', value: p => p.project_number, render: p => <strong>{p.project_number}</strong> },
        { header: 'Klant', value: p => p.company_name },
        { header: 'Project', value: p => p.project_name },
        { header: 'PL', value: p => p.pl || '—' },
        { header: 'Waarde', value: p => p.project_value, render: p => formatEuro(p.project_value), align: 'right' },
    ];

    const excelNotForecastCols = [
        { header: 'Nr', value: p => p.projectnummer, render: p => <strong>{p.projectnummer}</strong> },
        { header: 'Omschrijving', value: p => p.omschrijving },
        { header: 'PL', value: p => p.pl || '—' },
        { header: 'BBI', value: p => p.bbi, render: p => formatEuro(p.bbi), align: 'right' },
    ];

    return (
        <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
            <PageHeader
                icon="fact_check"
                title="OHW Check"
                subtitle="Planning en projecten in de tool naast OnderhandenWerk_FF.xlsx (SharePoint of upload)"
                rightActions={(
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            size="small" variant="outlined" startIcon={<OpenInNewIcon />}
                            href="https://airtable.com/apphMT5j6gtUnMY1p/tblUVT2dDpafP8rjq"
                            target="_blank"
                        >
                            Airtable
                        </Button>
                        <Button
                            size="small" variant="outlined" startIcon={<OpenInNewIcon />}
                            href="/revenue-forecast"
                        >
                            Forecast
                        </Button>
                    </Box>
                )}
            />

            {/* Planning in de tool naast het OHW-werkboek — de engine leest het bestand zelf van SharePoint */}
            <PlanningVsOhw search={search} formatEuro={formatEuro} />

            {/* Upload zone */}
            {!data && !loading && (
                <Paper
                    variant="outlined"
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    sx={{
                        p: 6, textAlign: 'center', cursor: 'pointer',
                        border: '2px dashed', borderColor: dragOver ? 'primary.main' : '#ddd',
                        bgcolor: dragOver ? 'primary.50' : '#fafafa',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: 'primary.main', bgcolor: '#f5f5ff' },
                    }}
                    onClick={() => document.getElementById('excel-upload').click()}
                >
                    <CloudUploadIcon sx={{ fontSize: 48, color: '#999', mb: 1 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Sleep het OHW Excel bestand hierheen
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        of klik om te selecteren (OnderhandenWerk_FF.xlsx)
                    </Typography>
                    <Button variant="contained" startIcon={<CloudUploadIcon />}>
                        Selecteer bestand
                    </Button>
                    <input
                        id="excel-upload"
                        type="file"
                        accept=".xlsx,.xls"
                        hidden
                        onChange={handleFileInput}
                    />
                </Paper>
            )}

            {loading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
                    <CircularProgress size={48} />
                    <Typography color="text.secondary">Excel verwerken &amp; Airtable ophalen...</Typography>
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Results */}
            {data && (
                <>
                    {/* Summary cards */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                        <SummaryCard label="In Excel" value={data.summary.excel_total} color="#0d6efd" />
                        <SummaryCard label="Airtable Open" value={data.summary.airtable_open} color="#0d6efd" />
                        <SummaryCard label="In beide" value={data.summary.in_both} color="#28a745" />
                        <SummaryCard
                            label="Mist in Excel"
                            value={data.summary.missing_in_excel}
                            color="#dc3545"
                            sub={formatEuro(data.summary.total_missing_value)}
                        />
                        <SummaryCard label="Excel dicht in AT" value={data.summary.excel_not_in_airtable} color="#fd7e14" />
                    </Box>

                    {/* New check button */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Zoek op naam, klant, PL of nummer..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            sx={{ width: 350 }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                            }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained" size="small"
                                startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                                onClick={handleDownloadAnnotated}
                                disabled={!uploadedFile || downloading}
                            >
                                Download met deltas
                            </Button>
                            <Button
                                variant="outlined" size="small" startIcon={<CloudUploadIcon />}
                                onClick={() => { setData(null); setSearch(''); setUploadedFile(null); }}
                            >
                                Nieuwe check
                            </Button>
                        </Box>
                    </Box>

                    {/* Tabs */}
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                        <Tab label={`Mist in Excel (${data.summary.missing_in_excel})`} />
                        <Tab label={`Excel open, AT dicht (${data.summary.excel_not_in_airtable})`} />
                        {data.has_forecast && (
                            <Tab label={`Forecast delta's (${data.summary.forecast_not_in_excel + data.summary.excel_not_in_forecast})`} />
                        )}
                    </Tabs>

                    {/* Tab 0: Missing in Excel */}
                    {tab === 0 && (
                        <>
                            {data.missing_in_excel.length === 0 ? (
                                <Alert severity="success">Alle Airtable-opdrachten staan in de Excel!</Alert>
                            ) : (
                                <>
                                    {/* Group by status */}
                                    {Object.entries(
                                        data.missing_in_excel.reduce((acc, p) => {
                                            const s = p.status || 'Onbekend';
                                            if (!acc[s]) acc[s] = [];
                                            acc[s].push(p);
                                            return acc;
                                        }, {})
                                    )
                                    .sort(([a], [b]) => {
                                        const order = ['In progress', 'On Hold'];
                                        return (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b));
                                    })
                                    .map(([status, projects]) => (
                                        <CollapsibleSection
                                            key={status}
                                            title={status}
                                            count={projects.length}
                                            color={status === 'In progress' ? '#dc3545' : status === 'On Hold' ? '#fd7e14' : '#6c757d'}
                                            totalValue={projects.reduce((sum, p) => sum + (Number(p.opdrachtwaarde) || 0), 0)}
                                        >
                                            <ProjectTable projects={projects} columns={missingCols} search={search} />
                                        </CollapsibleSection>
                                    ))}
                                </>
                            )}
                        </>
                    )}

                    {/* Tab 1: Excel open but Airtable closed */}
                    {tab === 1 && (
                        <CollapsibleSection
                            title="In Excel (open) maar niet open in Airtable"
                            count={data.excel_not_in_airtable.length}
                            color="#fd7e14"
                        >
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Deze projecten staan in de Excel zonder "afgerond/gefactureerd" status,
                                maar zijn in Airtable al afgerond of geannuleerd.
                            </Typography>
                            <ProjectTable
                                projects={data.excel_not_in_airtable}
                                columns={excelNotAirtableCols}
                                search={search}
                            />
                        </CollapsibleSection>
                    )}

                    {/* Tab 2: Forecast deltas */}
                    {tab === 2 && data.has_forecast && (
                        <>
                            <CollapsibleSection
                                title="In Forecast maar NIET in Excel"
                                count={data.forecast_not_in_excel.length}
                                color="#6f42c1"
                                totalValue={data.forecast_not_in_excel.reduce((sum, f) => sum + (f.project_value || 0), 0)}
                            >
                                <ProjectTable
                                    projects={data.forecast_not_in_excel}
                                    columns={forecastCols}
                                    search={search}
                                    emptyText="Alle Forecast-projecten staan in de Excel"
                                />
                            </CollapsibleSection>

                            <CollapsibleSection
                                title="In Excel (open + BBI) maar NIET in Forecast"
                                count={data.excel_not_in_forecast.length}
                                color="#6f42c1"
                            >
                                <ProjectTable
                                    projects={data.excel_not_in_forecast}
                                    columns={excelNotForecastCols}
                                    search={search}
                                    emptyText="Alle Excel-projecten met BBI staan in de Forecast"
                                />
                            </CollapsibleSection>
                        </>
                    )}
                </>
            )}
        </Box>
    );
}
